import { motion } from "framer-motion";
import { User, Cloud, Braces, Boxes, Database, DatabaseBackup } from "lucide-react";
import { SectionHeader, Reveal } from "./Reveal";
import { CLOUD_HIGHLIGHTS } from "../../data/site";
import { EASE } from "../../lib/motion";

const STEPS = [
  { icon: User, label: "Usuario" },
  { icon: Cloud, label: "Cloud" },
  { icon: Braces, label: "API" },
  { icon: Boxes, label: "Servicios" },
  { icon: Database, label: "Database" },
  { icon: DatabaseBackup, label: "Backup" },
];

export default function CloudArchitecture() {
  return (
    <div className="grid items-center gap-14 lg:grid-cols-2 lg:gap-10">
      <div>
        <SectionHeader
          eyebrow="Cloud & Infraestructura"
          title="Tu operación. En la nube."
          description="Accede a la información de tu organización desde cualquier lugar y reduce la dependencia de infraestructura local."
        />
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          variants={{ hidden: {}, show: { transition: { staggerChildren: 0.07 } } }}
          className="mt-10 grid gap-4 sm:grid-cols-2"
        >
          {CLOUD_HIGHLIGHTS.map((h) => (
            <motion.div
              key={h.title}
              variants={{ hidden: { opacity: 0, y: 18 }, show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: EASE } } }}
              className="card-lift rounded-2xl border border-border bg-card p-5 hover:border-electric/40"
              data-testid={`cloud-highlight-${h.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              <div className="flex items-center gap-2.5">
                <h.icon className="h-4 w-4 text-electric" strokeWidth={1.8} />
                <h3 className="text-sm font-semibold">{h.title}</h3>
              </div>
              <p className="mt-1.5 text-[13px] leading-relaxed text-muted-foreground">{h.desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>

      <Reveal delay={0.1}>
        <div className="relative overflow-hidden rounded-3xl border border-border bg-card p-8 shadow-card sm:p-10" data-testid="cloud-pipeline">
          <div className="absolute inset-0 grid-bg opacity-60 [mask-image:radial-gradient(ellipse_at_center,black,transparent_75%)]" aria-hidden="true" />
          <p className="relative font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">Arquitectura cloud</p>
          <div className="relative mt-6 flex flex-col">
            {STEPS.map((s, i) => (
              <div key={s.label}>
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.4 }}
                  transition={{ delay: i * 0.14, duration: 0.55, ease: EASE }}
                  className="flex items-center gap-4"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-border bg-surface text-brand">
                    <s.icon className="h-5 w-5" strokeWidth={1.7} />
                  </span>
                  <span className="font-display text-sm font-semibold sm:text-base">{s.label}</span>
                  {i === 0 && <span className="ml-auto hidden font-mono text-[10px] uppercase tracking-widest text-muted-foreground sm:inline">Punto de acceso</span>}
                  {i === STEPS.length - 1 && <span className="ml-auto hidden font-mono text-[10px] uppercase tracking-widest text-emerald-600 dark:text-emerald-400 sm:inline">Continuidad</span>}
                </motion.div>
                {i < STEPS.length - 1 && (
                  <div className="relative ml-[21px] flex h-9 justify-start overflow-hidden" aria-hidden="true">
                    <span className="ml-px h-full w-px bg-gradient-to-b from-border via-electric/50 to-border" />
                    <motion.span
                      className="absolute left-[-2.5px] h-1.5 w-1.5 rounded-full bg-electric"
                      initial={{ top: "-10%", opacity: 0 }}
                      whileInView={{ top: ["-10%", "110%"], opacity: [0, 1, 0] }}
                      viewport={{ once: false, amount: 0.2 }}
                      transition={{ delay: 0.3 + i * 0.14, duration: 1.1, repeat: Infinity, repeatDelay: 1.4, ease: "easeInOut" }}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </Reveal>
    </div>
  );
}
